import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { supabase } from "./lib/supabase";
import "./drawing-viewer.css";

pdfjsLib.GlobalWorkerOptions.workerSrc=pdfWorker;

type DrawingRecord={id:string;drawing_number:string;title:string|null;revision:string|null;file_name:string|null;file_path:string|null};
type ViewerState={drawing:DrawingRecord;url:string}|null;
type PlacementRequest={weldId:string;projectId:string;companyId:string;drawingId:string;drawingNumber:string|null;weldLabel:string;shareToken:string|null};
type Placement={
 id:string;company_id:string;project_id:string;drawing_id:string;drawing_revision:string|null;weld_id:string;page_number:number;
 x:number;y:number;width:number;height:number;status:"current"|"needs_review";
 project_weld_register?:{weld_number:string; joint_id:string|null; share_token:string|null; share_enabled:boolean}|null;
};
type DraftPlacement={id?:string;x:number;y:number;width:number;height:number;page_number:number;weldId:string;companyId:string;projectId:string;weldLabel:string;shareToken:string|null};

const clamp=(value:number,min:number,max:number)=>Math.min(max,Math.max(min,value));
const qrUrl=(token:string|null|undefined)=>token?`${window.location.origin}/?weld=${encodeURIComponent(token)}`:"";
async function makeVectorQr(token:string|null|undefined){if(!token)return "";const svg=await QRCode.toString(qrUrl(token),{type:"svg",margin:1,errorCorrectionLevel:"M"});return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;}

export default function DrawingViewer(){
 const [viewer,setViewer]=useState<ViewerState>(null);
 const [loading,setLoading]=useState(false);
 const [error,setError]=useState("");
 const [scale,setScale]=useState(1);
 const [offset,setOffset]=useState({x:0,y:0});
 const [confirmDelete,setConfirmDelete]=useState(false);
 const [deleting,setDeleting]=useState(false);
 const [placementRequest,setPlacementRequest]=useState<PlacementRequest|null>(null);
 const [placements,setPlacements]=useState<Placement[]>([]);
 const [qrImages,setQrImages]=useState<Record<string,string>>({});
 const [draft,setDraft]=useState<DraftPlacement|null>(null);
 const [savingPlacement,setSavingPlacement]=useState(false);
 const [selectedPlacementId,setSelectedPlacementId]=useState<string|null>(null);
 const [pageNumber,setPageNumber]=useState(1);
 const [pageCount,setPageCount]=useState(1);
 const [pdf,setPdf]=useState<any>(null);
 const pdfCanvas=useRef<HTMLCanvasElement|null>(null);
 const surface=useRef<HTMLDivElement|null>(null);
 const drag=useRef<{mode:"pan"|"move"|"resize";x:number;y:number;ox:number;oy:number;ow?:number;oh?:number}|null>(null);

 const loadPlacements=useCallback(async(drawing:DrawingRecord)=>{
  const {data,error}=await supabase.from("drawing_weld_qr_placements")
   .select("*,project_weld_register(weld_number,joint_id,share_token,share_enabled)")
   .eq("drawing_id",drawing.id);
  if(error){setPlacements([]);return}
  const rows=(data||[]) as Placement[];
  const stale=rows.filter(p=>(p.drawing_revision||"")!==(drawing.revision||"")&&p.status!=="needs_review");
  if(stale.length)await supabase.from("drawing_weld_qr_placements").update({status:"needs_review",updated_at:new Date().toISOString()}).in("id",stale.map(x=>x.id));
  setPlacements(rows.map(p=>stale.some(s=>s.id===p.id)?{...p,status:"needs_review"}:p));
  const images:Record<string,string>={};
  await Promise.all(rows.map(async p=>{
   const token=p.project_weld_register?.share_token;
   if(!token)return;
   images[p.id]=await makeVectorQr(token);
  }));
  setQrImages(images);
 },[]);

 const openDrawing=useCallback(async(drawingId:string,request:PlacementRequest|null=null)=>{
  setLoading(true);setError("");setConfirmDelete(false);
  const {data,error:recordError}=await supabase.from("drawings").select("id,drawing_number,title,revision,file_name,file_path").eq("id",drawingId).maybeSingle();
  if(recordError||!data?.file_path){setLoading(false);setError(recordError?.message||"Drawing file could not be opened.");return}
  const {data:signed,error:signedError}=await supabase.storage.from("project-documents").createSignedUrl(data.file_path,3600);
  setLoading(false);
  if(signedError||!signed?.signedUrl){setError(signedError?.message||"Drawing file could not be opened.");return}
  const drawing=data as DrawingRecord;
  setScale(1);setOffset({x:0,y:0});setPageNumber(1);setPageCount(1);setPdf(null);setViewer({drawing,url:signed.signedUrl});setPlacementRequest(request);setDraft(null);setSelectedPlacementId(null);
  await loadPlacements(drawing);
 },[loadPlacements]);

 useEffect(()=>{
  const onClick=async(event:MouseEvent)=>{
   const target=event.target as HTMLElement;
   if(target.closest("button,a,input,select,textarea"))return;
   const card=target.closest(".drawing-grid article") as HTMLElement|null;
   if(!card)return;
   const number=card.querySelector("h3")?.textContent?.trim();
   const fileName=[...card.querySelectorAll("small")].map(x=>x.textContent?.trim()||"").find(x=>/\.(pdf|png|jpe?g|tiff?|webp)$/i.test(x));
   if(!number||!fileName)return;
   event.preventDefault();
   const {data}=await supabase.from("drawings").select("id").eq("drawing_number",number).eq("file_name",fileName).order("created_at",{ascending:false}).limit(1).maybeSingle();
   if(data?.id)void openDrawing(String(data.id));
  };
  document.addEventListener("click",onClick,true);
  return()=>document.removeEventListener("click",onClick,true);
 },[openDrawing]);

 useEffect(()=>{
  const openRequested=()=>{
   try{
    const raw=window.sessionStorage.getItem("arcvault:place-weld-qr");
    if(!raw)return;
    const request=JSON.parse(raw) as PlacementRequest;
    window.sessionStorage.removeItem("arcvault:place-weld-qr");
    if(request?.drawingId)void openDrawing(request.drawingId,request);
   }catch{setError("ArcVault could not open the drawing placement request.")}
  };
  window.addEventListener("arcvault:place-weld-qr",openRequested);
  openRequested();
  return()=>window.removeEventListener("arcvault:place-weld-qr",openRequested);
 },[openDrawing]);

 const isPdf=Boolean(viewer?.drawing.file_name?.toLowerCase().endsWith(".pdf"));

 useEffect(()=>{
  if(!viewer||!isPdf)return;
  let cancelled=false;
  void pdfjsLib.getDocument(viewer.url).promise.then(doc=>{if(cancelled)return;setPdf(doc);setPageCount(doc.numPages||1)}).catch(()=>setError("ArcVault could not render this PDF drawing."));
  return()=>{cancelled=true};
 },[viewer,isPdf]);

 useEffect(()=>{
  if(!pdf||!pdfCanvas.current)return;
  let cancelled=false;
  void pdf.getPage(pageNumber).then((page:any)=>{
   if(cancelled||!pdfCanvas.current)return;
   const viewport=page.getViewport({scale:1.5});
   const canvas=pdfCanvas.current;
   const context=canvas.getContext("2d");
   if(!context)return;
   canvas.width=Math.ceil(viewport.width);canvas.height=Math.ceil(viewport.height);
   canvas.style.width=`${Math.ceil(viewport.width)}px`;canvas.style.height=`${Math.ceil(viewport.height)}px`;
   return page.render({canvasContext:context,viewport}).promise;
  }).catch(()=>setError("ArcVault could not render this drawing page."));
  return()=>{cancelled=true};
 },[pdf,pageNumber]);

 useEffect(()=>{
  if(!viewer)return;
  const onKey=(e:KeyboardEvent)=>{if(e.key==="Escape"&&!confirmDelete)setViewer(null)};
  window.addEventListener("keydown",onKey);
  return()=>window.removeEventListener("keydown",onKey);
 },[viewer,confirmDelete]);

 const zoom=(delta:number)=>setScale(s=>clamp(Number((s+delta).toFixed(2)),0.5,4));
 const reset=()=>{setScale(1);setOffset({x:0,y:0})};
 const wheelZoom=(e:React.WheelEvent<HTMLDivElement>)=>{e.preventDefault();zoom(e.deltaY<0?0.15:-0.15)};

 const startPan=(e:React.PointerEvent<HTMLDivElement>)=>{
  if((e.target as HTMLElement).closest(".drawing-qr-overlay"))return;
  if(scale<=1)return;
  drag.current={mode:"pan",x:e.clientX,y:e.clientY,ox:offset.x,oy:offset.y};
  e.currentTarget.setPointerCapture(e.pointerId);
 };
 const movePointer=(e:React.PointerEvent<HTMLDivElement>)=>{
  if(!drag.current)return;
  if(drag.current.mode==="pan"){setOffset({x:drag.current.ox+(e.clientX-drag.current.x),y:drag.current.oy+(e.clientY-drag.current.y)});return}
  if(!draft||!surface.current)return;
  const rect=surface.current.getBoundingClientRect();
  const dx=(e.clientX-drag.current.x)/rect.width,dy=(e.clientY-drag.current.y)/rect.height;
  if(drag.current.mode==="move")setDraft({...draft,x:clamp(drag.current.ox+dx,0,1-draft.width),y:clamp(drag.current.oy+dy,0,1-draft.height)});
  else{
   const size=clamp(Math.max((drag.current.ow||draft.width)+dx,(drag.current.oh||draft.height)+dy),0.045,0.25);
   setDraft({...draft,width:size,height:size,x:clamp(draft.x,0,1-size),y:clamp(draft.y,0,1-size)});
  }
 };
 const stopPointer=()=>{drag.current=null};

 const beginPlacement=()=>{
  if(!placementRequest)return;
  const existing=placements.find(p=>p.weld_id===placementRequest.weldId&&p.page_number===pageNumber&&(p.drawing_revision||"")===(viewer?.drawing.revision||""));
  const meta={weldId:placementRequest.weldId,companyId:placementRequest.companyId,projectId:placementRequest.projectId,weldLabel:placementRequest.weldLabel,shareToken:placementRequest.shareToken};
  setSelectedPlacementId(existing?.id||null);
  setDraft(existing?{id:existing.id,x:Number(existing.x),y:Number(existing.y),width:Number(existing.width),height:Number(existing.height),page_number:pageNumber,...meta}:{x:.78,y:.1,width:.09,height:.09,page_number:pageNumber,...meta});
 };

 const beginEditPlacement=(placement:Placement)=>{
  const weld=placement.project_weld_register;
  setPlacementRequest(null);
  setSelectedPlacementId(placement.id);
  setDraft({id:placement.id,x:Number(placement.x),y:Number(placement.y),width:Number(placement.width),height:Number(placement.height),page_number:placement.page_number,weldId:placement.weld_id,companyId:placement.company_id,projectId:placement.project_id,weldLabel:weld?.joint_id||weld?.weld_number||"Weld",shareToken:weld?.share_token||null});
 };

 const savePlacement=async()=>{
  if(!viewer||!draft)return;
  setSavingPlacement(true);setError("");
  const {data:{user}}=await supabase.auth.getUser();
  const payload={company_id:draft.companyId,project_id:draft.projectId,drawing_id:viewer.drawing.id,drawing_revision:viewer.drawing.revision||null,weld_id:draft.weldId,page_number:draft.page_number,x:draft.x,y:draft.y,width:draft.width,height:draft.height,status:"current",created_by:user?.id||null,updated_at:new Date().toISOString()};
  let result;
  if(draft.id)result=await supabase.from("drawing_weld_qr_placements").update(payload).eq("id",draft.id).select().single();
  else{
   const existingQuery=supabase.from("drawing_weld_qr_placements").select("id").eq("weld_id",draft.weldId).eq("drawing_id",viewer.drawing.id).eq("page_number",draft.page_number);
   const {data:existing}=viewer.drawing.revision?await existingQuery.eq("drawing_revision",viewer.drawing.revision).maybeSingle():await existingQuery.is("drawing_revision",null).maybeSingle();
   result=existing?.id?await supabase.from("drawing_weld_qr_placements").update(payload).eq("id",existing.id).select().single():await supabase.from("drawing_weld_qr_placements").insert(payload).select().single();
  }
  setSavingPlacement(false);
  if(result.error){setError(result.error.message);return}
  setDraft(null);setSelectedPlacementId(result.data?.id||draft.id||null);await loadPlacements(viewer.drawing);
 };

 const removePlacement=async(id:string)=>{
  const placement=placements.find(p=>p.id===id);
  const label=placement?.project_weld_register?.joint_id||placement?.project_weld_register?.weld_number||"this weld";
  if(!window.confirm(`Remove the QR placement for ${label} from this drawing?`))return;
  const {error}=await supabase.from("drawing_weld_qr_placements").delete().eq("id",id);
  if(error){setError(error.message);return}
  setSelectedPlacementId(null);
  setDraft(null);
  if(viewer)await loadPlacements(viewer.drawing);
 };

 const copyQrLink=async(token:string|null|undefined)=>{
  const url=qrUrl(token);
  if(!url){setError("This weld does not have an active QR link.");return}
  try{
   await navigator.clipboard.writeText(url);
  }catch{
   setError("Could not copy the QR link.");
  }
 };

 const removeDrawing=async()=>{
  if(!viewer||deleting)return;
  setDeleting(true);setError("");
  if(viewer.drawing.file_path){
   const storageResult=await supabase.storage.from("project-documents").remove([viewer.drawing.file_path]);
   if(storageResult.error){setDeleting(false);setError(storageResult.error.message);return}
  }
  const removed={id:viewer.drawing.id,drawing_number:viewer.drawing.drawing_number};
  const {error:deleteError}=await supabase.from("drawings").delete().eq("id",viewer.drawing.id);
  setDeleting(false);
  if(deleteError){setError(deleteError.message);return}
  setViewer(null);setConfirmDelete(false);
  window.dispatchEvent(new CustomEvent("arcvault:record-removed",{detail:{type:"drawing",...removed}}));
 };

 const currentPlacements=placements.filter(p=>p.page_number===pageNumber&&(p.drawing_revision||"")===(viewer?.drawing.revision||""));
 const stalePlacements=placements.filter(p=>p.status==="needs_review");
 const selectedPlacement=currentPlacements.find(p=>p.id===selectedPlacementId)||null;
 const selectedToken=selectedPlacement?.project_weld_register?.share_token||null;
 const selectedLabel=selectedPlacement?.project_weld_register?.joint_id||selectedPlacement?.project_weld_register?.weld_number||"Weld";

 return <>{loading&&createPortal(<div className="drawing-viewer-loading">Opening drawing…</div>,document.body)}{error&&createPortal(<div className="drawing-viewer-error" onClick={()=>setError("")}>{error}</div>,document.body)}{viewer&&createPortal(<div className="drawing-viewer-backdrop" onMouseDown={e=>{if(e.target===e.currentTarget&&!confirmDelete)setViewer(null)}}>
  <section className="drawing-viewer-modal" role="dialog" aria-modal="true" aria-label={`Drawing ${viewer.drawing.drawing_number}`}>
   <header><div><p className="eyebrow">CONTROLLED DRAWING</p><h2>{viewer.drawing.drawing_number}{viewer.drawing.revision?` · ${viewer.drawing.revision}`:""}</h2><small>{viewer.drawing.title||viewer.drawing.file_name}</small>{stalePlacements.length?<span className="drawing-qr-review-note">{stalePlacements.length} QR placement{stalePlacements.length===1?"":"s"} from another revision need review</span>:null}</div><div className="drawing-viewer-controls">{isPdf&&pageCount>1?<><button type="button" disabled={pageNumber<=1} onClick={()=>setPageNumber(p=>Math.max(1,p-1))}>‹</button><span>{pageNumber}/{pageCount}</span><button type="button" disabled={pageNumber>=pageCount} onClick={()=>setPageNumber(p=>Math.min(pageCount,p+1))}>›</button></>:null}<button type="button" onClick={()=>zoom(-0.25)} aria-label="Zoom out">−</button><span>{Math.round(scale*100)}%</span><button type="button" onClick={()=>zoom(0.25)} aria-label="Zoom in">＋</button><button type="button" onClick={reset}>Fit</button>{placementRequest?<button type="button" className="drawing-viewer-place" onClick={beginPlacement}>{draft?"Reset QR":"Place QR"}</button>:null}<button type="button" className="drawing-viewer-delete" onClick={()=>setConfirmDelete(true)}>Remove drawing</button><button type="button" className="drawing-viewer-close" onClick={()=>setViewer(null)} aria-label="Close">×</button></div></header>
   {draft?<div className="drawing-qr-toolbar"><b>Editing QR for weld {draft.weldLabel}</b><span>Click and hold the QR to move it. Drag the blue corner handle to resize.</span><button type="button" className="primary" disabled={savingPlacement} onClick={savePlacement}>{savingPlacement?"Saving…":"Save placement"}</button><button type="button" onClick={()=>setDraft(null)}>Cancel</button></div>:selectedPlacement?<div className="drawing-qr-toolbar"><b>QR for weld {selectedLabel}</b><span>Selected. Edit its position, open the controlled link, copy the link, or remove it.</span><button type="button" onClick={()=>beginEditPlacement(selectedPlacement)}>Edit placement</button>{selectedToken?<><a className="drawing-qr-link-button" href={qrUrl(selectedToken)} target="_blank" rel="noreferrer">Open QR link</a><button type="button" onClick={()=>void copyQrLink(selectedToken)}>Copy link</button></>:null}<button type="button" className="danger" onClick={()=>void removePlacement(selectedPlacement.id)}>Remove QR</button></div>:placementRequest?<div className="drawing-qr-toolbar"><b>Place QR for weld {placementRequest.weldLabel}</b><span>Place it on the welding-symbol tail, then save its position.</span><button type="button" onClick={beginPlacement}>Start placement</button></div>:null}
   <div className={`drawing-viewer-stage ${scale>1?"can-pan":""}`} onWheel={wheelZoom} onPointerDown={startPan} onPointerMove={movePointer} onPointerUp={stopPointer} onPointerCancel={stopPointer}>
    <div className="drawing-viewer-canvas" style={{transform:`translate(${offset.x}px, ${offset.y}px) scale(${scale})`}}>
     <div className="drawing-page-surface" ref={surface}>
      {isPdf?<canvas ref={pdfCanvas}/>:<img src={viewer.url} alt={`Drawing ${viewer.drawing.drawing_number}`} draggable={false}/>}
      {currentPlacements.filter(p=>p.id!==draft?.id).map(p=>{const label=p.project_weld_register?.joint_id||p.project_weld_register?.weld_number||"Weld";const selected=p.id===selectedPlacementId;return <div key={p.id} className={`drawing-qr-overlay saved ${selected?"selected":""}`} style={{left:`${Number(p.x)*100}%`,top:`${Number(p.y)*100}%`,width:`${Number(p.width)*100}%`,height:`${Number(p.height)*100}%`}} title={`${label} · click to select · double-click to edit`} onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();setSelectedPlacementId(p.id)}} onDoubleClick={e=>{e.stopPropagation();beginEditPlacement(p)}}>{qrImages[p.id]?<img src={qrImages[p.id]} alt={`QR for ${label}`}/>:null}<small>{label}</small>{selected?<button type="button" className="drawing-qr-remove" aria-label={`Remove QR for ${label}`} title="Remove QR" onClick={e=>{e.stopPropagation();void removePlacement(p.id)}}>×</button>:null}</div>})}
      {draft?<DraftQr draft={draft} onMoveStart={(e)=>{e.stopPropagation();drag.current={mode:"move",x:e.clientX,y:e.clientY,ox:draft.x,oy:draft.y};(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)}} onResizeStart={(e)=>{e.stopPropagation();drag.current={mode:"resize",x:e.clientX,y:e.clientY,ox:draft.x,oy:draft.y,ow:draft.width,oh:draft.height};(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)}}/>:null}
     </div>
    </div>
   </div>
   <footer>{draft||placementRequest||selectedPlacement?"QR placements are stored separately from the controlled drawing. The original drawing file is never modified.":"Use + / − or the mouse wheel to zoom. Click a QR to manage it; double-click it to edit its position."}</footer>
   {confirmDelete?<div className="drawing-delete-confirm"><div><b>Remove {viewer.drawing.drawing_number}?</b><p>This will permanently remove the drawing record and uploaded file.</p><div className="drawing-delete-actions"><button type="button" onClick={()=>setConfirmDelete(false)}>Cancel</button><button type="button" className="danger" disabled={deleting} onClick={removeDrawing}>{deleting?"Removing…":"Yes, remove drawing"}</button></div></div></div>:null}
  </section>
 </div>,document.body)}</>;
}

function DraftQr({draft,onMoveStart,onResizeStart}:{draft:DraftPlacement;onMoveStart:(e:React.PointerEvent<HTMLDivElement>)=>void;onResizeStart:(e:React.PointerEvent<HTMLButtonElement>)=>void}){
 const [image,setImage]=useState("");
 useEffect(()=>{void makeVectorQr(draft.shareToken).then(setImage)},[draft.shareToken]);
 return <div className="drawing-qr-overlay editing" style={{left:`${draft.x*100}%`,top:`${draft.y*100}%`,width:`${draft.width*100}%`,height:`${draft.height*100}%`}} onPointerDown={onMoveStart}>{image?<img src={image} alt={`QR for ${draft.weldLabel}`}/>:<span>QR</span>}<small>{draft.weldLabel}</small><button type="button" className="drawing-qr-resize" aria-label="Resize QR" onPointerDown={onResizeStart}/></div>
}
